import React, { useEffect, useMemo, useRef, useState } from 'react'
import useTrans from '@hooks/useTrans'
import { cn } from '@utils/safeTs'
import type { UtenteType } from '@utils/types'
import Avatar from '../Avatar/Avatar'
import { ChevronDown, Profile2 } from '@components/icons'
import styles from './IncaricatoDropdown.module.sass'

export interface IncaricatoDropdownProps {
  value?: number
  options?: UtenteType[]
  onChange?: (utente?: UtenteType) => void
  size?: 'md' | 'sm'
  className?: string
  disabled?: boolean
  menuAlign?: 'left' | 'right'
}

const getFullName = (utente?: UtenteType) =>
  [utente?.nome, utente?.cognome].filter(Boolean).join(' ').trim()

const IncaricatoDropdown = ({
  value,
  options = [],
  onChange,
  size = 'md',
  className,
  disabled = false,
  menuAlign = 'left',
}: IncaricatoDropdownProps) => {

  const t = useTrans()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const toggleOpen = () => {
    if (disabled) return
    setOpen((prev) => !prev)
  }

  const handleSelect = (utente?: UtenteType) => {
    onChange?.(utente)
    setOpen(false)
    buttonRef.current?.focus()
  }

  const menuId = `incaricato-dropdown-${value ?? 'none'}`
  const selectedUtente = useMemo(() => options.filter(o => o.id === value)?.[0], [value, options])

  return (
    <div
      className={cn(styles.root, className)}
      ref={containerRef}
    >
      <button
        ref={buttonRef}
        type="button"
        className={cn(styles.trigger, open && styles['trigger--open'])}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={toggleOpen}
        disabled={disabled}
      >
        {selectedUtente ? (
          <Avatar 
            utente={selectedUtente} 
            variant="incaricato" 
            showTooltip={false} 
            size={size}
          />
        ) : (
          <span className={styles.placeholderAvatar} aria-hidden>
            <Profile2 />
          </span>
        )}
        <ChevronDown className={styles.chevron} aria-hidden />
      </button>
      {open ? (
        <div
          id={menuId}
          role="listbox"
          className={cn(styles.menu, styles[`menu--${menuAlign}`])}
        >
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              role="option"
              aria-selected={option.id === value}
              className={cn(styles.option, option.id === value && styles['option--selected'])}
              onClick={() => handleSelect(option)}
            >
              {getFullName(option) || t('Senza nome')}
            </button>
          ))}
          {!options.length ? (
            <div className={styles.empty}>{t('Nessun incaricato disponibile')}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default IncaricatoDropdown
